import React, { useState, useEffect, useCallback, useRef } from 'react';
import { PermissionGuard } from '@components/PermissionGuard';
import { Company } from '../../types/company';
import { VariableFeeUpsertPayload } from '../../types/variableFee';
import companyService from '../../services/companyService';
import variableFeeService from '../../services/variableFeeService';
import { Building2, ChevronLeft, ChevronRight, Check, Loader2 } from 'lucide-react';

const MONTHS = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril',
  'Maio', 'Junho', 'Julho', 'Agosto',
  'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

interface CellState {
  tax_percent: string;
  seller_commission_percent: string;
  dirty_tax: boolean;
  dirty_commission: boolean;
  saving: boolean;
}

export const VariableFeesView: React.FC = () => {
  const currentYear = new Date().getFullYear();

  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
  const [year, setYear] = useState(currentYear);
  const [rows, setRows] = useState<CellState[]>(Array(12).fill(null).map(() => ({
    tax_percent: '',
    seller_commission_percent: '',
    dirty_tax: false,
    dirty_commission: false,
    saving: false,
  })));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Load companies on mount
  useEffect(() => {
    const load = async () => {
      try {
        const data = await companyService.getCompanies();
        setCompanies(data);
        if (data.length > 0 && !selectedCompanyId) {
          setSelectedCompanyId(data[0].company_id);
        }
      } catch {
        setError('Falha ao carregar empresas');
      }
    };
    load();
  }, []);

  // Load variable fees when company or year changes
  useEffect(() => {
    if (!selectedCompanyId) return;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await variableFeeService.getByCompanyAndYear({
          company_id: selectedCompanyId,
          year,
        });
        console.debug('[VariableFeesView] data from API:', data);
        // Map API data to 12-month grid
        const newRows: CellState[] = Array(12).fill(null).map((_, idx) => {
          const refDate = `${year}-${String(idx + 1).padStart(2, '0')}-01`;
          const found = data.find(d => {
            // Normalize to first 10 chars to handle possible ISO datetime strings
            const normalized = d.reference_date ? String(d.reference_date).substring(0, 10) : '';
            console.debug(`[VariableFeesView] comparing refDate="${refDate}" with db="${normalized}" (raw="${d.reference_date}")`);
            return normalized === refDate;
          });
          return {
            tax_percent: found?.tax_percent != null ? String(found.tax_percent) : '',
            seller_commission_percent: found?.seller_commission_percent != null ? String(found.seller_commission_percent) : '',
            dirty_tax: false,
            dirty_commission: false,
            saving: false,
          };
        });
        setRows(newRows);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Falha ao carregar dados');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [selectedCompanyId, year]);

  const buildReferenceDate = (monthIndex: number) =>
    `${year}-${String(monthIndex + 1).padStart(2, '0')}-01`;

  const handleCellChange = (monthIdx: number, field: 'tax_percent' | 'seller_commission_percent', value: string) => {
    // Allow empty, or numeric with up to 2 decimals (max 99.99)
    if (value !== '' && !/^\d{0,2}(\.\d{0,2})?$/.test(value)) return;
    setRows(prev => prev.map((row, i) => {
      if (i !== monthIdx) return row;
      return {
        ...row,
        [field]: value,
        ...(field === 'tax_percent' ? { dirty_tax: true } : { dirty_commission: true }),
      };
    }));
  };

  const saveCell = useCallback(async (monthIdx: number) => {
    if (!selectedCompanyId) return;
    const row = rows[monthIdx];
    if (!row.dirty_tax && !row.dirty_commission) return;

    const taxVal = row.tax_percent.trim() === '' ? null : parseFloat(row.tax_percent);
    const commVal = row.seller_commission_percent.trim() === '' ? null : parseFloat(row.seller_commission_percent);

    // If both empty, delete the record
    if (taxVal === null && commVal === null) {
      setRows(prev => prev.map((r, i) => i === monthIdx ? { ...r, saving: true } : r));
      try {
        await variableFeeService.delete(selectedCompanyId, buildReferenceDate(monthIdx));
        setRows(prev => prev.map((r, i) => i === monthIdx ? { ...r, saving: false, dirty_tax: false, dirty_commission: false } : r));
        showSuccess(monthIdx);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erro ao salvar');
        setRows(prev => prev.map((r, i) => i === monthIdx ? { ...r, saving: false } : r));
      }
      return;
    }

    const payload: VariableFeeUpsertPayload = {
      company_id: selectedCompanyId,
      reference_date: buildReferenceDate(monthIdx),
      tax_percent: taxVal,
      seller_commission_percent: commVal,
    };

    setRows(prev => prev.map((r, i) => i === monthIdx ? { ...r, saving: true } : r));
    try {
      await variableFeeService.upsert(payload);
      setRows(prev => prev.map((r, i) => i === monthIdx ? { ...r, saving: false, dirty_tax: false, dirty_commission: false } : r));
      showSuccess(monthIdx);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar');
      setRows(prev => prev.map((r, i) => i === monthIdx ? { ...r, saving: false } : r));
    }
  }, [rows, selectedCompanyId, year]);

  const showSuccess = (monthIdx: number) => {
    setSuccessMsg(`${MONTHS[monthIdx]} salvo com sucesso`);
    setTimeout(() => setSuccessMsg(null), 2000);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, monthIdx: number, field: 'tax_percent' | 'seller_commission_percent') => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveCell(monthIdx);
      // Move to next cell
      const currentInputIdx = monthIdx * 2 + (field === 'tax_percent' ? 0 : 1);
      const nextInput = inputRefs.current[currentInputIdx + 1] || inputRefs.current[currentInputIdx + 2];
      if (nextInput) nextInput.focus();
    } else if (e.key === 'Tab') {
      // Tab will save current cell
      saveCell(monthIdx);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      const col = field === 'tax_percent' ? 0 : 1;
      const nextIdx = (monthIdx + 1) * 2 + col;
      if (inputRefs.current[nextIdx]) inputRefs.current[nextIdx]!.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const col = field === 'tax_percent' ? 0 : 1;
      const prevIdx = (monthIdx - 1) * 2 + col;
      if (prevIdx >= 0 && inputRefs.current[prevIdx]) inputRefs.current[prevIdx]!.focus();
    }
  };

  const handleBlur = (monthIdx: number) => {
    saveCell(monthIdx);
  };

  return (
    <PermissionGuard permission="variable_fees.read" showError>
      <div className="p-6 max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-900">Custos Variáveis</h1>
          <p className="text-sm text-slate-500 mt-1">
            Gerencie as tarifas mensais de impostos e comissão de vendedor por empresa.
          </p>
        </div>

        {/* Tabs */}
        <div className="border-b border-slate-200 mb-6">
          <nav className="flex gap-4">
            <button className="px-4 py-2 text-sm font-medium text-emerald-700 border-b-2 border-emerald-600">
              Impostos e Comissão
            </button>
            {/* Future tabs placeholder */}
          </nav>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-4 mb-6 bg-white border border-slate-200 rounded-lg p-4">
          {/* Company dropdown */}
          <div className="flex items-center gap-2 flex-1 min-w-[280px]">
            <Building2 size={18} className="text-slate-400" />
            <select
              className="input flex-1"
              value={selectedCompanyId ?? ''}
              onChange={e => setSelectedCompanyId(Number(e.target.value))}
            >
              <option value="" disabled>Selecione uma empresa</option>
              {companies.map(c => (
                <option key={c.company_id} value={c.company_id}>
                  {c.name} — {c.document}
                </option>
              ))}
            </select>
          </div>

          {/* Year selector */}
          <div className="flex items-center gap-2">
            <button
              className="p-1.5 rounded hover:bg-slate-100 text-slate-600"
              onClick={() => setYear(y => y - 1)}
              title="Ano anterior"
            >
              <ChevronLeft size={20} />
            </button>
            <span className="text-lg font-semibold text-slate-800 min-w-[60px] text-center">
              {year}
            </span>
            <button
              className="p-1.5 rounded hover:bg-slate-100 text-slate-600 disabled:opacity-30 disabled:cursor-not-allowed"
              onClick={() => setYear(y => y + 1)}
              disabled={year >= currentYear}
              title="Próximo ano"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        </div>

        {/* Messages */}
        {error && (
          <div className="mb-4 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
            {error}
          </div>
        )}
        {successMsg && (
          <div className="mb-4 px-4 py-3 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm flex items-center gap-2">
            <Check size={16} />
            {successMsg}
          </div>
        )}

        {/* Table */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={24} className="animate-spin text-slate-400" />
            <span className="ml-2 text-slate-500">Carregando...</span>
          </div>
        ) : !selectedCompanyId ? (
          <div className="text-center py-20 text-slate-400">
            Selecione uma empresa para visualizar os custos variáveis.
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left px-4 py-3 font-semibold text-slate-700 w-40">Mês</th>
                  <th className="text-center px-4 py-3 font-semibold text-slate-700">Impostos (%)</th>
                  <th className="text-center px-4 py-3 font-semibold text-slate-700">Comissão Vendedor (%)</th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody>
                {MONTHS.map((month, idx) => {
                  const row = rows[idx];
                  const isFuture = year === currentYear && idx > new Date().getMonth();
                  return (
                    <tr
                      key={idx}
                      className={`border-b border-slate-100 ${isFuture ? 'opacity-40' : ''} ${row?.dirty_tax || row?.dirty_commission ? 'bg-amber-50/50' : 'hover:bg-slate-50'}`}
                    >
                      <td className="px-4 py-2 font-medium text-slate-700">
                        {month}/{year}
                      </td>
                      <td className="px-4 py-1">
                        <input
                          ref={el => { inputRefs.current[idx * 2] = el; }}
                          type="text"
                          inputMode="decimal"
                          className="w-full text-center border border-slate-200 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 disabled:bg-slate-100"
                          placeholder="0.00"
                          value={row?.tax_percent ?? ''}
                          disabled={isFuture}
                          onChange={e => handleCellChange(idx, 'tax_percent', e.target.value)}
                          onKeyDown={e => handleKeyDown(e, idx, 'tax_percent')}
                          onBlur={() => handleBlur(idx)}
                        />
                      </td>
                      <td className="px-4 py-1">
                        <input
                          ref={el => { inputRefs.current[idx * 2 + 1] = el; }}
                          type="text"
                          inputMode="decimal"
                          className="w-full text-center border border-slate-200 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 disabled:bg-slate-100"
                          placeholder="0.00"
                          value={row?.seller_commission_percent ?? ''}
                          disabled={isFuture}
                          onChange={e => handleCellChange(idx, 'seller_commission_percent', e.target.value)}
                          onKeyDown={e => handleKeyDown(e, idx, 'seller_commission_percent')}
                          onBlur={() => handleBlur(idx)}
                        />
                      </td>
                      <td className="px-2 py-1 text-center">
                        {row?.saving && <Loader2 size={14} className="animate-spin text-emerald-500 inline" />}
                        {!row?.saving && (row?.dirty_tax || row?.dirty_commission) && (
                          <span className="text-amber-500 text-xs" title="Pressione Enter para salvar">●</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="px-4 py-2 bg-slate-50 border-t border-slate-200 text-xs text-slate-500">
              Pressione <kbd className="px-1 py-0.5 bg-slate-200 rounded text-slate-700 font-mono">Enter</kbd> ou <kbd className="px-1 py-0.5 bg-slate-200 rounded text-slate-700 font-mono">Tab</kbd> para salvar. Use setas ↑↓ para navegar.
            </div>
          </div>
        )}
      </div>
    </PermissionGuard>
  );
};
